import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendSingleRequest } from '../../apis';
import { ILineResource } from '../../entities/farms.entities';
import { showFeedback } from '../../store/ui/ui.actions';
import { numToStr, toMillisecond, toSecond } from '../../lib/common.helpers';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Input,
  Subtitle,
} from '../shared';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import TimeTrackInput, {
  toSecTimeTracks,
} from '../shared/input/TimeTrackInput';
import FilesWall from '../shared/pictures-wall/FilesWall';
import moment from 'moment';
import { IFileWall, ITimeRange } from '../../entities/general.entities';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import {
  SelectDetailedBudgetEnabled,
  SelectFarmsByFarmID,
  SelectInventoriesByFarmID,
  SelectIsBudgetVisible,
} from '../../store/extra/extra.selector';
import { selectProfile } from '../../store/auth/auth.selector';
import { IExpenseRequest } from '../../entities/budget.entities';
import { initialBudgetForm } from '../../lib/form.helpers';
import OperatorBudgetForm from './OperatorBudgetForm';
import { parseImageUrlToFileItem } from '../../lib/common.helpers';
import { AreaClosedConfirmModal } from '../CommonViews';
import { loadQueueData } from '../../store/farms/farms.actions';
import SelectBoat from '../farmform/SelectBoat';
import { TaggingManageForm } from '../lines/LineTagButton';
import { loadTags } from '../../store/automation/automation.actions';
import SelectBatchSeed from '../farmform/SelectBatchSeed';

const typeOptions = [
  {
    id: 'VISUAL_ASSESSMENT',
    value: 'VISUAL_ASSESSMENT',
    label: 'Visual Assessment',
  },
  { id: 'LINE_CLEANING', value: 'LINE_CLEANING', label: 'Line Cleaning' },
  {
    id: 'GENERAL_MAINTENANCE',
    value: 'GENERAL_MAINTENANCE',
    label: 'General Maintenance',
  },
];

type typeField = 'VISUAL_ASSESSMENT' | 'LINE_CLEANING' | 'GENERAL_MAINTENANCE';

interface IInventoryManage {
  inventory_id?: number;
  status: 'IN' | 'OUT';
  quantity: number | null;
}

interface IForm {
  maintain_date: number;
  batch_seed_id: number | null;
  type: typeField;
  comment: string | null;
  boat_id?: number | null;
  image_pdf_files?: IFileWall[];

  time_tracks?: ITimeRange[];

  cost?: number | null;
  expenses?: IExpenseRequest[];

  inventory_manage?: IInventoryManage;

  tagged_ids?: number[] | null;
}

const defaultForm: IForm = {
  maintain_date: moment().toDate().getTime(),
  batch_seed_id: null,
  type: 'VISUAL_ASSESSMENT' as typeField,
  comment: '',
  boat_id: null,
  image_pdf_files: [],
  time_tracks: [],
};

interface Props {
  visible: boolean;
  title: string;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  data?: any;
  lineData: ILineResource;
  onlyView?: boolean;
  updateId?: number;
  confirmBtnLabel?: string;
  overConfirm?: (data: any) => any;
  lineChangeable?: boolean;
}

const MaintenanceModal = ({
  visible,
  title,
  onCancel,
  onConfirm,
  data,
  lineData,
  onlyView,
  updateId,
  confirmBtnLabel,
  overConfirm,
  lineChangeable,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const inventories = SelectInventoriesByFarmID(lineData.farm_id);
  const visibleCost = SelectIsBudgetVisible(lineData.farm_id);
  const detailedBudgetEnabled = SelectDetailedBudgetEnabled(lineData.farm_id);
  const curBoat = useSelector(selectProfile)?.current_boat;

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [form, setForm] = useState<IForm>({
    ...defaultForm,
    batch_seed_id: data?.batch_seed_id ?? defaultForm.batch_seed_id,
    boat_id: curBoat?.id,
    expenses: detailedBudgetEnabled
      ? [{ name: '', quantity: null, unit_price: null, amount: null }]
      : undefined,
  });
  const [curFarm, setCurFarm] = useState(
    farmsData.find(x => x.id === lineData.farm_id),
  );
  const [curLine, setCurLine] = useState(lineData);

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        type: 'error',
        message,
        duration: -1,
      }),
    );
  const updateForm = (key: string, val: any) => {
    setForm({ ...form, [key]: val });
  };
  const handleManageInventoryChange = (e: any) => {
    if (e.target.checked) {
      setForm({
        ...form,
        inventory_manage: {
          inventory_id: undefined,
          status: 'OUT',
          quantity: null,
        },
      });
    } else {
      setForm({ ...form, inventory_manage: undefined });
    }
  };
  const handleLineChange = (v: string) => {
    if (!curFarm) return;
    const line = curFarm.lines.find(x => x.id === Number(v));
    if (!line?.growing_cycle) return;
    setCurLine(line);
  };
  const validData = () => {
    if (!form.type) {
      showError(translate(lang, 'Please select maintenance type.'));
      return null;
    }
    if (
      form.image_pdf_files &&
      form.image_pdf_files.length <= 0 &&
      (form.comment?.length ?? 0) <= 0
    ) {
      showError(translate(lang, '_empty_comment_images_allowed'));
      return null;
    }
    if (form.inventory_manage !== undefined) {
      if (!form.inventory_manage.inventory_id) {
        showError(
          translate(lang, 'Inventory is required for inventory management.'),
        );
        return null;
      }
      if (
        !form.inventory_manage.quantity ||
        form.inventory_manage.quantity <= 0
      ) {
        showError(
          translate(
            lang,
            'Quantity must be greater than zero for inventory management.',
          ),
        );
        return null;
      }
    }
    let res: any = {
      ...form,
      maintain_date: toSecond(form.maintain_date),
      expenses: form.expenses?.filter(x => x.name && x.amount),
      line_id: curLine.id,
      seeding_id: curLine.growing_cycle?.main_seed.id,
    };
    if (res.time_tracks) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (tt === false) {
        showError(translate(lang, '_invalid_time_range'));
        return null;
      }
      res.time_tracks = tt;
    }
    return res;
  };
  const handleConfirmClick = async () => {
    const form = validData();
    if (!form) return;

    setDisabled(true);
    if (!!overConfirm) {
      const r = await overConfirm(form);
      if (r !== true) {
        setDisabled(false);
        showError(r?.message ?? translate(lang, 'Operation cancelled'));
      }
      return;
    }
    const res = !updateId
      ? await sendSingleRequest(
          form,
          'POST',
          'api/farm/line/seed/maintenance',
          true,
        )
      : await sendSingleRequest(
          form,
          'PUT',
          `api/farm/line/seed/maintenance/${updateId}`,
          true,
        );
    if (res.status) {
      if (form.tagged_ids) {
        dispatch(loadTags());
      }
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
      onConfirm(res.data);
      dispatch(loadQueueData());
    } else {
      showError(translate(lang, res.data?.message ?? 'Invalid request'));
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (data) {
      let tmp: any = {
        maintain_date: toMillisecond(data.maintain_date),
        type: data.type,
        comment: data.comment,
        batch_seed_id: data.batch_seed_id ?? null,
        boat_id: data.boat_id ?? curBoat?.id,
        image_pdf_files: [],
        inventory_manage: data.inventory_manage ?? undefined,
        tagged_ids: data.tagged_ids ?? null,
      };
      if (data.time_tracks) {
        tmp.time_tracks = data.time_tracks.map((x: any) => ({
          start_time: toMillisecond(x.start_time),
          finish_time: toMillisecond(x.finish_time),
        }));
      }
      if (visibleCost) {
        tmp = { ...tmp, ...initialBudgetForm(detailedBudgetEnabled, data) };
      }
      if (data.image_pdf_files && data.image_pdf_files.length > 0) {
        tmp.image_pdf_files = data.image_pdf_files.map((x: any) =>
          typeof x === 'object' && x !== null ? x : parseImageUrlToFileItem(x),
        );
      }
      setForm(tmp);
    }
  }, [data, visibleCost, detailedBudgetEnabled]);

  if (!isConfirmed) {
    return (
      <AreaClosedConfirmModal
        farmId={lineData.farm_id}
        onCancel={onCancel}
        onConfirm={() => setIsConfirmed(true)}
      />
    );
  }

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: x.line_name,
  }));
  const inventoriesOption = inventories.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: x.name,
  }));

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={detailedBudgetEnabled ? 800 : 600}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, title)}
          </Subtitle>
        </div>
        <div className='assessment-modal'>
          {lineChangeable && (
            <div className='mb-17 d-flex'>
              <div className='w-100 mr-7'>
                <Dropdown
                  label={translate(lang, 'Select farm')}
                  options={farmOptions}
                  value={curFarm?.id.toString()}
                  onChange={v =>
                    setCurFarm(farmsData.find(x => x.id === Number(v)))
                  }
                />
              </div>
              <div className='w-100 ml-7'>
                <Dropdown
                  label={translate(lang, 'Select line')}
                  options={lineOptions ?? []}
                  value={
                    !!lineOptions &&
                    lineOptions.some(x => Number(x.id) === curLine.id)
                      ? curLine.id.toString()
                      : undefined
                  }
                  onChange={handleLineChange}
                />
              </div>
            </div>
          )}
          <div className='mb-17 mt-17'>
            <Datepicker
              label={translate(lang, 'Maintained date')}
              defaultValue={form.maintain_date}
              onChange={e =>
                e && updateForm('maintain_date', e.toDate().getTime())
              }
            />
          </div>
          <div className='mb-17'>
            <SelectBatchSeed
              label={translate(lang, 'This is seed specific maintenance')}
              lineData={curLine}
              seedings={curLine.growing_cycle?.seedings ?? []}
              value={form.batch_seed_id}
              onChange={v => updateForm('batch_seed_id', v)}
            />
          </div>
          <div className='mb-17'>
            <Dropdown
              label={translate(lang, 'Type')}
              placeholder={translate(lang, 'Type')}
              options={typeOptions}
              value={form.type}
              onChange={(val, evt) => updateForm('type', val)}
              disabled={onlyView}
            />
          </div>
          <div className='mb-17'>
            <Input
              label={translate(lang, 'Comment')}
              placeholder={translate(lang, '_write_desc_here')}
              type='textarea'
              value={form.comment ?? ''}
              dataType='comment'
              onChange={e => updateForm('comment', e.target.value)}
              disabled={onlyView}
            />
          </div>
          {!updateId && (
            <div className='mb-17'>
              <CheckboxButton
                label={translate(lang, 'Manage inventory')}
                checked={form.inventory_manage !== undefined}
                onChange={handleManageInventoryChange}
                disabled={onlyView}
              />
            </div>
          )}
          {form.inventory_manage !== undefined && (
            <div className='mb-17'>
              <div className='mb-17'>
                <Dropdown
                  label={translate(lang, 'Select inventory')}
                  options={inventoriesOption}
                  value={form.inventory_manage.inventory_id?.toString()}
                  onChange={val =>
                    updateForm('inventory_manage', {
                      ...form.inventory_manage,
                      inventory_id: Number(val),
                    })
                  }
                  disabled={onlyView}
                />
              </div>
              <div className='d-flex mb-17'>
                <div className='w-100 mr-7'>
                  <Dropdown
                    label={translate(lang, 'Type')}
                    options={[
                      {
                        id: 'IN',
                        value: 'IN',
                        label: translate(lang, '_remove_line_inv'),
                      },
                      {
                        id: 'OUT',
                        value: 'OUT',
                        label: translate(lang, 'Added to the line'),
                      },
                    ]}
                    value={form.inventory_manage.status}
                    onChange={val =>
                      updateForm('inventory_manage', {
                        ...form.inventory_manage,
                        status: val,
                      })
                    }
                    disabled={onlyView}
                  />
                </div>
                <div className='w-100 ml-7'>
                  <Input
                    label={translate(lang, 'Quantity')}
                    placeholder={translate(lang, 'Enter quantity')}
                    type='number'
                    value={numToStr(form.inventory_manage.quantity)}
                    onChange={e =>
                      updateForm('inventory_manage', {
                        ...form.inventory_manage,
                        quantity: e.target.value
                          ? Number(e.target.value)
                          : null,
                      })
                    }
                    disabled={onlyView}
                  />
                </div>
              </div>
            </div>
          )}
          {form.time_tracks && (
            <TimeTrackInput
              className='mt-24'
              data={form.time_tracks}
              onChange={t => updateForm('time_tracks', t)}
              baseDate={form.maintain_date}
              disabled={onlyView}
            />
          )}
          <SelectBoat
            className='mb-17'
            value={form.boat_id}
            onChange={v => updateForm('boat_id', v)}
            disabled={onlyView}
          />
          <div className='mb-17'>
            <FilesWall
              label={translate(lang, 'Images & PDF Files')}
              fileList={form.image_pdf_files}
              onChange={v => updateForm('image_pdf_files', v)}
              disabled={onlyView}
            />
          </div>
          {!!visibleCost && (
            <OperatorBudgetForm
              detailedBudgetEnabled={detailedBudgetEnabled}
              form={form}
              onChange={setForm}
              disabled={onlyView}
            />
          )}
        </div>
        {!updateId && (
          <TaggingManageForm
            className='mb-17 mt-17'
            lineData={curLine}
            tagIDs={form.tagged_ids}
            onChange={ids => updateForm('tagged_ids', ids)}
          />
        )}
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={handleConfirmClick}
          disabled={disabled || onlyView}
        >
          {translate(lang, confirmBtnLabel ?? 'Confirm')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default MaintenanceModal;
